import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getadvanceSettings, setadvanceSettings } from "../../network/cipAPI";
import DupontButton from "../../components/DupontButton";
import InputBox from "../../components/InputBox";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { OverlayLoader } from "../../components/PageLoading";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import PopoverText from "../../components/PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";
import "./index.scss";
import { MINIMUM_SETTINGS_OPTIONS } from "./columns.config";
import { useSelector } from "react-redux";

const INPUT_LIMITS = {
  time1: { min: 3, max: 44 },
  time2: { min: 45, max: 300 },
};

const INITIAL_ERRORS = {
  time1: null,
  time2: null,
};

const MinimumSettings = ({
  setIsProceedEnabled,
  setView,
  fileId,
  setHasMinimumSettingsError,
  isEdit,
}) => {
  const { t } = useTranslation();
  const reduxTime1 = useSelector((state) => state.time1);
  const reduxTime2 = useSelector((state) => state.time2);
  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [isSaved, setIsSaved] = useState(false);
  const [timeSettings, setTimeSettings] = useState({ time1: "", time2: "" });
  const [originalTimeSettings, setOriginalTimeSettings] = useState({
    time1: "",
    time2: "",
  });

  // Get the config for time fields from columns.config.js
  const timeFieldsConfig = MINIMUM_SETTINGS_OPTIONS(t);
  const timeFieldKeys = Object.keys(timeFieldsConfig);

  useEffect(() => {
    if (!fileId) {
      setTimeSettings({ time1: "", time2: "" });
      setOriginalTimeSettings({ time1: "", time2: "" });
      return;
    }
    // Fetch advance settings for time1 and time2
    const fetchAdvanceSettings = async () => {
      try {
        const response = await getadvanceSettings();
        if (response && response.data) {
          const loaded = {
            time1: response.data.time1?.toString() || "",
            time2: response.data.time2?.toString() || "",
          };
          setTimeSettings(loaded);
          setOriginalTimeSettings(loaded);
          setErrors(INITIAL_ERRORS); // Clear errors when loading advance settings
        }
      } catch (e) {
        // Optionally handle error
      }
    };
    fetchAdvanceSettings();
  }, [fileId]);

  useEffect(() => {
    // Sync with Redux if values change
    setTimeSettings((prev) => ({
      ...prev,
      time1: reduxTime1?.toString() || "",
      time2: reduxTime2?.toString() || "",
    }));

    // Clear errors if redux values are valid
    const newErrors = { ...errors };
    if (reduxTime1 !== undefined && reduxTime1 !== null && reduxTime1 !== "") {
      newErrors.time1 = null;
    }
    if (reduxTime2 !== undefined && reduxTime2 !== null && reduxTime2 !== "") {
      newErrors.time2 = null;
    }
    setErrors(newErrors);
  }, [reduxTime1, reduxTime2]);

  //update Proceed button state on error changes
  useEffect(() => {
    if (setHasMinimumSettingsError) {
      const hasError = Object.values(errors).some((err) => !!err);
      setHasMinimumSettingsError(hasError);
    }
  }, [errors, setHasMinimumSettingsError]);

  const onTimeInputChange = (value, key) => {
    setTimeSettings((prev) => ({ ...prev, [key]: value }));
    setIsSaved(false); // Reset isSaved on edit
    setIsProceedEnabled && setIsProceedEnabled(true); // Disable Proceed on edit
    let error = null;
    const { min, max } = INPUT_LIMITS[key] || {};
    if (value.trim() === "") {
      error = `${t("defaultInformationStrings.cannotLess")} ${min}`;
    } else if (!isNaN(parseFloat(value))) {
      const numValue = parseFloat(value);
      if (
        (min !== undefined && numValue < min) ||
        (max !== undefined && numValue > max)
      ) {
        error = `${t("defaultInformationStrings.shouldBetween")} ${min} ${t(
          "defaultInformationStrings.upper_Limit"
        )}  ${max}`;
      } else if (min !== undefined && numValue < min) {
        error = `${t("defaultInformationStrings.cannotLess")} ${min}`;
      } else if (max !== undefined && numValue > max) {
        error = `${t("defaultInformationStrings.cannotGreater")} ${max}`;
      }
    }
    setErrors((prev) => ({ ...prev, [key]: error }));
  };

  const getConfirmButtonState = () => {
    const { time1, time2 } = timeSettings;
    // Check if both fields are non-empty and valid numbers
    const isValidNumber = (val) =>
      val !== "" && !isNaN(parseFloat(val)) && isFinite(val);
    const hasAllInputs =
      time1.trim() !== "" &&
      time2.trim() !== "" &&
      isValidNumber(time1) &&
      isValidNumber(time2);
    const noErrors = ["time1", "time2"].every((field) => !errors[field]);
    if (isSaved) return true;
    // In edit mode, only enable if changes are made
    if (isEdit) {
      const changed =
        time1 !== originalTimeSettings.time1 ||
        time2 !== originalTimeSettings.time2;
      return !(hasAllInputs && noErrors && changed);
    }
    // In create mode, original logic
    return !(hasAllInputs && noErrors);
  };

  const confirmMinimumSettings = async () => {
    setShowOverLayLoader(true);
    try {
      const advancePayload = {
        time1: parseFloat(timeSettings.time1),
        time2: parseFloat(timeSettings.time2),
      };
      await setadvanceSettings(advancePayload);
      setIsProceedEnabled && setIsProceedEnabled(false);
      setView && setView(2);
      setIsSaved(true);
      // Update originalTimeSettings to the latest saved values
      setOriginalTimeSettings({
        time1: timeSettings.time1,
        time2: timeSettings.time2,
      });
      createNotification(
        SUCCESS_NOTIFICATION,
        t("minimumSettings.confirmSuccess"),
        ""
      );
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("minimumSettings.confirmError"),
        err.message
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  return (
    <div className="lightGrayBorder padding10 position-relative">
      {showOverLayLoader && <OverlayLoader customHeight="200px" />}
      <div className="box-title-info box-title text-align-left">
        <span>{t("minimumSettings.title")}</span>
        <PopoverText
          descriptor={<img src={InfoIconGreen} alt="icon" />}
          descriptionText={t("minimumSettings.description")}
        />
      </div>
      <div className="flex-ai-center container-padding position-relative">
        <div className="grid-container-column">
          {timeFieldKeys.map((key, idx) => {
            const field = timeFieldsConfig[key];
            // If fileId is not present, show empty value
            const value = fileId ? timeSettings[field.key] : "";
            return (
              <div className="grid-item-column" key={field.key}>
                <div>
                  {idx === 0 && (
                    <label style={{ display: "block", marginBottom: 20 }}>
                      {t("minimumSettings.labelBackwash")}
                    </label>
                  )}
                  {idx === 1 && (
                    <label style={{ display: "block", marginBottom: 20 }}>
                      {t("minimumSettings.labelCEB")}
                    </label>
                  )}
                  <label>{field.name}</label>
                </div>
                <div className="width-dropdown">
                  <InputBox
                    name={field.key}
                    key={field.key}
                    value={value}
                    onChange={(val) => onTimeInputChange(val, field.key)}
                    validator={TextFieldValidator.NUMBER_WITH_DECIMAL}
                    min={INPUT_LIMITS[field.key].min}
                    errorMsg={errors[field.key]}
                    maxLength={10}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="reference-button ">
          <DupontButton
            disabled={getConfirmButtonState()}
            customClass="margin-left-10 "
            onClick={confirmMinimumSettings}
          >
            {t("minimumSettings.confirmButton")}
          </DupontButton>
        </div>
      </div>
    </div>
  );
};

export default MinimumSettings;
